import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Icon, Select, Text, TextField } from '@shopify/polaris';
import { DeleteIcon, ExternalIcon, SearchIcon } from '@shopify/polaris-icons';
import { formatFileDate, getBadgeColor, getFileExtension } from './fileTypes';
import './FileListBrowser.css';

const sortOptions = [
  { label: 'Name A–Z', value: 'name-asc' },
  { label: 'Name Z–A', value: 'name-desc' },
  { label: 'Newest first', value: 'date-desc' },
  { label: 'Oldest first', value: 'date-asc' }
];

const stopPropagation = (event) => event.stopPropagation();

const compareFiles = (sortValue) => {
  const [key, direction] = sortValue.split('-');
  const factor = direction === 'desc' ? -1 : 1;

  return (a, b) => {
    if (key === 'date') {
      const aTime = new Date(a.modDate).getTime() || 0;
      const bTime = new Date(b.modDate).getTime() || 0;
      return (aTime - bTime) * factor;
    }
    return (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' }) * factor;
  };
};

// Lightweight replacement for Chonky's FileBrowser: a responsive file list with
// search, sorting, (multi-)selection, open and delete. Click selects, ctrl/cmd-click
// toggles, shift-click selects a range, double-click / Enter opens, Delete removes.
export default function FileListBrowser({ files = [], onOpenFile, onDeleteFiles, disabled = false, isDragActive = false, emptyMessage = 'No files yet.' }) {
  const [query, setQuery] = useState('');
  const [sortValue, setSortValue] = useState('name-asc');
  const [selectedIds, setSelectedIds] = useState([]);
  const anchorIdRef = useRef(null);

  const visibleFiles = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = needle ? files.filter((file) => (file.name || '').toLowerCase().includes(needle)) : files;
    return [...filtered].sort(compareFiles(sortValue));
  }, [files, query, sortValue]);

  // Drop selections for files that disappeared (deleted / refetched).
  useEffect(() => {
    setSelectedIds((current) => {
      const next = current.filter((id) => files.some((file) => file.id === id));
      return next.length === current.length ? current : next;
    });
  }, [files]);

  useEffect(() => {
    if (disabled) setSelectedIds([]);
  }, [disabled]);

  const selectedFiles = files.filter((file) => selectedIds.includes(file.id));

  const toggleFile = (fileId) => {
    anchorIdRef.current = fileId;
    setSelectedIds((current) => (current.includes(fileId) ? current.filter((id) => id !== fileId) : [...current, fileId]));
  };

  const handleRowClick = (event, fileId) => {
    if (disabled) return;

    if (event.shiftKey && anchorIdRef.current) {
      const ids = visibleFiles.map((file) => file.id);
      const from = ids.indexOf(anchorIdRef.current);
      const to = ids.indexOf(fileId);
      if (from !== -1 && to !== -1) {
        const range = ids.slice(Math.min(from, to), Math.max(from, to) + 1);
        setSelectedIds((current) => (event.ctrlKey || event.metaKey ? [...new Set([...current, ...range])] : range));
        return;
      }
    }

    if (event.ctrlKey || event.metaKey) {
      toggleFile(fileId);
      return;
    }

    anchorIdRef.current = fileId;
    setSelectedIds([fileId]);
  };

  const handleDelete = () => {
    if (disabled || selectedFiles.length === 0) return;
    onDeleteFiles?.(selectedFiles);
  };

  const handleRowKeyDown = (event, file, index) => {
    if (event.target !== event.currentTarget) return;

    if (event.key === 'Enter') {
      event.preventDefault();
      onOpenFile?.(file);
    } else if (event.key === ' ') {
      event.preventDefault();
      if (!disabled) toggleFile(file.id);
    } else if (event.key === 'Delete') {
      event.preventDefault();
      handleDelete();
    } else if (event.key === 'Escape') {
      setSelectedIds([]);
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const sibling = event.key === 'ArrowDown' ? event.currentTarget.nextElementSibling : event.currentTarget.previousElementSibling;
      sibling?.focus();
      if (event.shiftKey && !disabled) {
        const nextFile = visibleFiles[index + (event.key === 'ArrowDown' ? 1 : -1)];
        if (nextFile) setSelectedIds((current) => [...new Set([...current, file.id, nextFile.id])]);
      }
    }
  };

  const countLabel = selectedFiles.length > 0 ? `${selectedFiles.length} of ${files.length} selected` : `${files.length} ${files.length === 1 ? 'file' : 'files'}`;

  return (
    <div className={`sfb-root${isDragActive ? ' sfb-drag-active' : ''}`}>
      <div className="sfb-toolbar">
        <div className="sfb-search">
          <TextField
            label="Search files"
            labelHidden
            placeholder="Search"
            prefix={<Icon source={SearchIcon} />}
            value={query}
            onChange={setQuery}
            clearButton
            onClearButtonClick={() => setQuery('')}
            autoComplete="off"
            size="slim"
          />
        </div>
        <div className="sfb-sort">
          <Select label="Sort files" labelHidden options={sortOptions} value={sortValue} onChange={setSortValue} />
        </div>
        <div className="sfb-actions">
          <Text as="span" variant="bodySm" tone="subdued">
            {countLabel}
          </Text>
          {onDeleteFiles ? (
            <Button icon={DeleteIcon} tone="critical" size="slim" disabled={disabled || selectedFiles.length === 0} onClick={handleDelete}>
              Delete
            </Button>
          ) : null}
        </div>
      </div>

      <div className="sfb-list" role="listbox" aria-multiselectable="true" aria-label="Files">
        {visibleFiles.length === 0 ? (
          <div className="sfb-empty">
            <Text as="p" tone="subdued">
              {files.length === 0 ? emptyMessage : 'No files match your search.'}
            </Text>
          </div>
        ) : (
          visibleFiles.map((file, index) => {
            const isSelected = selectedIds.includes(file.id);
            const extension = getFileExtension(file.name);

            return (
              <div
                key={file.id}
                role="option"
                aria-selected={isSelected}
                tabIndex={0}
                className={`sfb-row${isSelected ? ' sfb-row-selected' : ''}`}
                onClick={(event) => handleRowClick(event, file.id)}
                onDoubleClick={() => onOpenFile?.(file)}
                onKeyDown={(event) => handleRowKeyDown(event, file, index)}
                title={file.name}
              >
                <span className="sfb-check" onClick={stopPropagation} onDoubleClick={stopPropagation}>
                  <Checkbox label={`Select ${file.name}`} labelHidden checked={isSelected} disabled={disabled} onChange={() => toggleFile(file.id)} />
                </span>
                <span className="sfb-badge" style={{ background: getBadgeColor(file.name) }}>
                  {extension.slice(0, 4) || 'file'}
                </span>
                <span className="sfb-main">
                  <span className="sfb-name">{file.name}</span>
                  <span className="sfb-date">{formatFileDate(file.modDate)}</span>
                </span>
                <span className="sfb-open" onClick={stopPropagation} onDoubleClick={stopPropagation}>
                  <Button variant="tertiary" icon={ExternalIcon} accessibilityLabel={`Open ${file.name} in SharePoint`} onClick={() => onOpenFile?.(file)} />
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
